import { useEffect, useState, type FormEvent } from 'react'
import { Button } from '@floorfile/ui'
import { useCopy } from '@floorfile/i18n'
import { useAuth } from '@/lib/auth'
import { appCopy } from '../copy'

/**
 * Company-mandated 2FA enrollment gate (SECURITY_&_UAC company-wide 2FA,
 * audit C-01). Rendered instead of the app shell while the session is
 * enrollment-only: the API refuses everything except the 2FA-setup surface,
 * so this screen is the only path forward. Finishing enrollment refreshes
 * the session, which promotes it to full permissions.
 */
export function EnrollTwoFactorPage() {
  const copy = useCopy(appCopy)
  const { authFetch, refreshSession, logout, user } = useAuth()
  const [enroll, setEnroll] = useState<{ secret: string; uri: string } | undefined>(undefined)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[] | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    async function start() {
      try {
        const res = await authFetch('/api/v1/auth/2fa/setup', { method: 'POST' })
        const body = (await res.json()) as {
          data?: { secret: string; uri: string }
          error?: { message: string }
        }
        if (!res.ok || !body.data) throw new Error(body.error?.message ?? copy.authCommon.couldNotStartTwoFactor)
        if (!cancelled) setEnroll(body.data)
      } catch (err) {
        if (!cancelled) setMessage(err instanceof Error ? err.message : copy.authCommon.couldNotStartTwoFactor)
      }
    }
    void start()
    return () => {
      cancelled = true
    }
  }, [authFetch, copy.authCommon.couldNotStartTwoFactor])

  async function verify(event: FormEvent) {
    event.preventDefault()
    setMessage(undefined)
    setBusy(true)
    try {
      const res = await authFetch('/api/v1/auth/2fa/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code }),
      })
      const body = (await res.json()) as { data?: { backupCodes: string[] }; error?: { message: string } }
      if (!res.ok || !body.data) throw new Error(body.error?.message ?? copy.authCommon.codeNotAccepted)
      setBackupCodes(body.data.backupCodes)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : copy.authCommon.codeNotAccepted)
    } finally {
      setBusy(false)
    }
  }

  async function finish() {
    setBusy(true)
    await refreshSession()
    setBusy(false)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6">
        <div>
          <h1 className="text-lg font-semibold">{copy.twoFactorEnrollment.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {copy.twoFactorEnrollment.required({ email: user?.email ?? '' })}
          </p>
        </div>

        {backupCodes ? (
          <div className="space-y-3 text-sm">
            <p>
              {copy.twoFactorEnrollment.enabled}
            </p>
            <ul className="grid grid-cols-2 gap-1 rounded-md bg-muted/50 p-3 font-mono text-xs">
              {backupCodes.map((backupCode) => (
                <li key={backupCode}>{backupCode}</li>
              ))}
            </ul>
            <Button className="w-full"
 onClick={() => void finish()}
 disabled={busy}

 >
              {busy ? copy.twoFactorEnrollment.signingIn : copy.twoFactorEnrollment.continue}
            </Button>
          </div>
        ) : enroll ? (
          <form onSubmit={(e) => void verify(e)} className="space-y-3 text-sm">
            <p>{copy.twoFactorEnrollment.instructions}</p>
            <p className="break-all rounded-md bg-muted/50 p-2 font-mono text-xs">{enroll.uri}</p>
            <p>
              {copy.twoFactorEnrollment.manualKey} <span className="font-mono text-xs">{enroll.secret}</span>
            </p>
            <input aria-label={copy.authCommon.sixDigitCode}
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={copy.authCommon.sixDigitCode}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2"
            />
            {message && <p className="text-sm text-destructive">{message}</p>}
            <Button className="w-full"
 type="submit"
 disabled={busy}

 >
              {busy ? copy.twoFactorEnrollment.checking : copy.twoFactorEnrollment.enable}
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">{message ?? copy.twoFactorEnrollment.preparing}</p>
        )}

        <Button
          variant="link"
          size="inline"
          onClick={() => void logout()}
          className="w-full text-center text-muted-foreground"
        >
          {copy.authCommon.signOut}
        </Button>
      </div>
    </div>
  )
}
